import React, { useMemo, useState } from "react";
import "./quote.css";

import logoAj from "../images/logo-ajc2s-horizontale.png";

const initialState = {
  // STEP 1 — Contact
  lastName: "",
  firstName: "",
  company: "",
  phone: "",
  email: "",
  callPreference: "",

  // STEP 2 — Chantier
  siteAddress: "",
  postalCode: "",
  city: "",
  chantierType: "",
  prestationType: "",
  prestationOther: "",

  buildingType: "",
  surfaceRange: "",
  timeline: "",

  // STEP 3
  description: "",
  photos: [],
  consent: false,
};

const PRESTATIONS_BY_CHANTIER = {
  Maintenance: [
    "Recherche de fuites/expertise",
    "Entretien",
    "Urgence",
    "Diagnostic",
    "Réparation",
    "Sécurisation",
    "Végétalisation",
    "Autres",
  ],
  "Travaux neufs": [
    "Etanchéite Bitume/PVC",
    "Bardage",
    "Charpente",
    "Renovatioin",
    "Dalletes",
    "Zinguerie",
    "Autres",
  ],
  Rénovation: [
    "Etanchéite Bitume/PVC",
    "Bardage",
    "Charpente",
    "Dalletes",
    "Zinguerie",
    "Autres",
  ],
};

function isEmailValid(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

function clampStep(s) {
  return Math.max(1, Math.min(3, s));
}

export default function QuoteFormPage() {
  const [data, setData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState("");
  const [step, setStep] = useState(1);
  const [isSending, setIsSending] = useState(false);

  const progress = step === 1 ? 33 : step === 2 ? 66 : 100;

  const prestationOptions = useMemo(() => {
    return PRESTATIONS_BY_CHANTIER[data.chantierType] || [];
  }, [data.chantierType]);

  const prestationLabel = useMemo(() => {
    if (data.prestationType === "Autres") {
      const other = data.prestationOther.trim();
      return other ? `Autres: ${other}` : "Autres";
    }
    return data.prestationType || "—";
  }, [data.prestationType, data.prestationOther]);

  function setField(key, value) {
    setData((prev) => {
      const next = { ...prev, [key]: value };

      if (key === "chantierType") {
        next.prestationType = "";
        next.prestationOther = "";
      }
      if (key === "prestationType" && value !== "Autres") {
        next.prestationOther = "";
      }

      return next;
    });

    setErrors((prev) => ({ ...prev, [key]: undefined }));
    setSuccess("");
  }

  function validateStep(current, s) {
    const e = {};

    if (s === 1) {
      if (!current.lastName.trim()) e.lastName = "Nom obligatoire";
      if (!current.firstName.trim()) e.firstName = "Prénom obligatoire";
      if (!current.phone.trim()) e.phone = "Téléphone obligatoire";

      if (!current.email.trim()) e.email = "E-mail obligatoire";
      else if (!isEmailValid(current.email)) e.email = "E-mail invalide";
    }

    if (s === 2) {
      if (!current.siteAddress.trim()) e.siteAddress = "Adresse du chantier obligatoire";
      if (!current.postalCode.trim()) e.postalCode = "Code postal obligatoire";
      if (!current.city.trim()) e.city = "Ville obligatoire";

      if (!current.chantierType) e.chantierType = "Choix obligatoire";
      if (!current.prestationType) e.prestationType = "Choix obligatoire";
      if (current.prestationType === "Autres" && !current.prestationOther.trim()) {
        e.prestationOther = "Merci de préciser";
      }
    }

    if (s === 3) {
      if (!current.description.trim()) e.description = "Message obligatoire";
      if (!current.consent) e.consent = "Consentement obligatoire";
    }

    return e;
  }

  function goNext() {
    setSuccess("");
    const eMap = validateStep(data, step);
    setErrors(eMap);
    if (Object.values(eMap).some(Boolean)) return;

    setStep((prev) => clampStep(prev + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function goPrev() {
    setSuccess("");
    setErrors({});
    setStep((prev) => clampStep(prev - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (isSending) return;

    setSuccess("");
    const eMap = validateStep(data, 3);
    setErrors(eMap);
    if (Object.values(eMap).some(Boolean)) return;

    const payload = {
      lastName: data.lastName.trim(),
      firstName: data.firstName.trim(),
      company: data.company.trim() || null,
      phone: data.phone.trim(),
      email: data.email.trim(),
      callPreference: data.callPreference || null,

      siteAddress: data.siteAddress.trim(),
      postalCode: data.postalCode.trim(),
      city: data.city.trim(),
      chantierType: data.chantierType,
      prestationType: prestationLabel,

      buildingType: data.buildingType || null,
      surfaceRange: data.surfaceRange || null,
      timeline: data.timeline || null,

      description: data.description.trim(),
      consent: data.consent,
      createdAt: new Date().toISOString(),
    };

    try {
      setIsSending(true);

      const fd = new FormData();
      fd.append("json", JSON.stringify(payload));
      data.photos.forEach((f) => fd.append("photos", f));

      const res = await fetch("https://devis.aj-construction2savoie.noxzi.fr/api/devis.php", {
        method: "POST",
        body: fd,
      });

      const out = await res.json().catch(() => null);

      if (!res.ok || !out || out.ok !== true) {
        throw new Error(out?.details || out?.error || "Envoi impossible (serveur mail).");
      }

      setSuccess("✅ Votre demande a bien été envoyée. Nous vous recontactons rapidement.");

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || "Erreur lors de l'envoi.");
      }

      setSuccess("✅ Votre demande a bien été envoyée. Nous vous recontactons rapidement.");
      setData(initialState);
      setStep(1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setSuccess("");
      setErrors((prev) => ({ ...prev, form: err?.message || "Une erreur est survenue." }));
    } finally {
      setIsSending(false);
    }
  }

  function handleReset() {
    setData(initialState);
    setErrors({});
    setSuccess("");
    setStep(1);
  }

  return (
    <div className="q-wrap q-wrapFull">
      <div className="q-shell">
        <aside className="q-side">
          <div className="q-sideInner">
            <img src={logoAj} alt="AJ Construction 2 Savoie" className="q-sideLogo" />

            <h2>Demande de devis</h2>
            <p>Maintenance · Étanchéité · Bardage</p>

            <div className="q-sideCard">
              <div className="t">Devis gratuit</div>
              <div className="s">Réponse rapide — Intervention Savoie</div>
            </div>

            <div className="q-sideSteps">
              <div className={`q-sideStep ${step >= 1 ? "on" : ""}`}>1 · Contact</div>
              <div className={`q-sideStep ${step >= 2 ? "on" : ""}`}>2 · Chantier</div>
              <div className={`q-sideStep ${step >= 3 ? "on" : ""}`}>3 · Message</div>
            </div>
          </div>
        </aside>

        <main className="q-main">
          <div className="q-card q-cardPremium q-cardFullHeight">
            <div className="q-brandTop q-brandTopPremium">
              <div className="q-brandLeft">
                <img src={logoAj} alt="AJ Construction 2 Savoie" className="q-logoImg" />
                <div className="q-brandText">
                  <div className="q-titleRow">
                    <b className="q-title">Demande de devis</b>
                    <span className="q-stepPill">
                      Étape <b>{step}</b>/3
                    </span>
                  </div>
                  <div className="q-sub2">Réponse rapide — Devis gratuit</div>
                </div>
              </div>
            </div>

            <div className="q-progressWrap">
              <div className="q-progressBar" style={{ width: `${progress}%` }} />
            </div>

            <div className="q-stepsRow">
              <div className={`q-stepItem ${step >= 1 ? "on" : ""}`}>
                <span className="dot" /> Contact
              </div>
              <div className={`q-stepItem ${step >= 2 ? "on" : ""}`}>
                <span className="dot" /> Chantier
              </div>
              <div className={`q-stepItem ${step >= 3 ? "on" : ""}`}>
                <span className="dot" /> Message
              </div>
            </div>

            {errors.form ? <div className="q-toast q-toastPremium">{errors.form}</div> : null}
            {success ? <div className="q-toast q-toastPremium">{success}</div> : null}

            <form onSubmit={handleSubmit} className="q-form" noValidate>
              {/* STEP 1 */}
              {step === 1 && (
                <div className="q-stepPanel">
                  <section className="q-section q-sectionPremium">
                    <div className="q-sectionTitle">
                      <div className="q-badge">1</div>
                      Informations client
                    </div>

                    <div className="q-fields q-two">
                      <div>
                        <label>
                          Nom <span className="q-req">*</span>
                        </label>
                        <input
                          value={data.lastName}
                          onChange={(e) => setField("lastName", e.target.value)}
                          className={errors.lastName ? "q-error" : ""}
                          autoComplete="family-name"
                        />
                        {errors.lastName ? <div className="q-errText">{errors.lastName}</div> : null}
                      </div>

                      <div>
                        <label>
                          Prénom <span className="q-req">*</span>
                        </label>
                        <input
                          value={data.firstName}
                          onChange={(e) => setField("firstName", e.target.value)}
                          className={errors.firstName ? "q-error" : ""}
                          autoComplete="given-name"
                        />
                        {errors.firstName ? <div className="q-errText">{errors.firstName}</div> : null}
                      </div>
                    </div>

                    <div className="q-fields q-one" style={{ marginTop: 12 }}>
                      <div>
                        <label>Entreprise</label>
                        <input
                          value={data.company}
                          onChange={(e) => setField("company", e.target.value)}
                          autoComplete="organization"
                          placeholder="(optionnel)"
                        />
                      </div>
                    </div>

                    <div className="q-fields q-two" style={{ marginTop: 12 }}>
                      <div>
                        <label>
                          Téléphone <span className="q-req">*</span>
                        </label>
                        <input
                          value={data.phone}
                          onChange={(e) => setField("phone", e.target.value)}
                          className={errors.phone ? "q-error" : ""}
                          autoComplete="tel"
                          inputMode="tel"
                          placeholder="06 ..."
                        />
                        {errors.phone ? <div className="q-errText">{errors.phone}</div> : null}
                      </div>

                      <div>
                        <label>
                          E-mail <span className="q-req">*</span>
                        </label>
                        <input
                          value={data.email}
                          onChange={(e) => setField("email", e.target.value)}
                          className={errors.email ? "q-error" : ""}
                          autoComplete="email"
                          inputMode="email"
                          placeholder="ex: contact@..."
                        />
                        {errors.email ? <div className="q-errText">{errors.email}</div> : null}
                      </div>
                    </div>

                    <div className="q-fields q-one" style={{ marginTop: 12 }}>
                      <div>
                        <label>Préférence de rappel</label>
                        <select
                          value={data.callPreference}
                          onChange={(e) => setField("callPreference", e.target.value)}
                        >
                          <option value="">— Peu importe —</option>
                          <option>Matin (8h–12h)</option>
                          <option>Midi (12h–14h)</option>
                          <option>Après-midi (14h–18h)</option>
                          <option>Soir (18h–20h)</option>
                        </select>
                        <div className="q-hint">
                          Ce n’est pas un rendez-vous : c’est juste une préférence si possible.
                        </div>
                      </div>
                    </div>
                  </section>

                  <div className="q-actions q-actionsPremium">
                    <button type="button" className="q-btn q-btnGhost" onClick={handleReset} disabled={isSending}>
                      Réinitialiser
                    </button>
                    <button type="button" className="q-btn q-btnPrimary" onClick={goNext} disabled={isSending}>
                      Continuer
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 2 */}
              {step === 2 && (
                <div className="q-stepPanel">
                  <section className="q-section q-sectionPremium">
                    <div className="q-sectionTitle">
                      <div className="q-badge">2</div>
                      Informations chantier
                    </div>

                    <div className="q-fields q-one">
                      <div>
                        <label>
                          Adresse du chantier <span className="q-req">*</span>
                        </label>
                        <input
                          value={data.siteAddress}
                          onChange={(e) => setField("siteAddress", e.target.value)}
                          className={errors.siteAddress ? "q-error" : ""}
                          autoComplete="street-address"
                        />
                        {errors.siteAddress ? <div className="q-errText">{errors.siteAddress}</div> : null}
                      </div>
                    </div>

                    <div className="q-fields q-two" style={{ marginTop: 12 }}>
                      <div>
                        <label>
                          Code postal <span className="q-req">*</span>
                        </label>
                        <input
                          value={data.postalCode}
                          onChange={(e) => setField("postalCode", e.target.value)}
                          className={errors.postalCode ? "q-error" : ""}
                          inputMode="numeric"
                          placeholder="73000"
                        />
                        {errors.postalCode ? <div className="q-errText">{errors.postalCode}</div> : null}
                      </div>

                      <div>
                        <label>
                          Ville <span className="q-req">*</span>
                        </label>
                        <input
                          value={data.city}
                          onChange={(e) => setField("city", e.target.value)}
                          className={errors.city ? "q-error" : ""}
                          autoComplete="address-level2"
                          placeholder="Chambéry"
                        />
                        {errors.city ? <div className="q-errText">{errors.city}</div> : null}
                      </div>
                    </div>

                    <div className="q-fields q-two" style={{ marginTop: 12 }}>
                      <div>
                        <label>
                          Type de chantier <span className="q-req">*</span>
                        </label>
                        <select
                          value={data.chantierType}
                          onChange={(e) => setField("chantierType", e.target.value)}
                          className={errors.chantierType ? "q-error" : ""}
                        >
                          <option value="">— Choisir —</option>
                          <option>Maintenance</option>
                          <option>Travaux neufs</option>
                          <option>Rénovation</option>
                        </select>
                        {errors.chantierType ? <div className="q-errText">{errors.chantierType}</div> : null}
                      </div>

                      <div>
                        <label>
                          Type de prestation <span className="q-req">*</span>
                        </label>
                        <select
                          value={data.prestationType}
                          onChange={(e) => setField("prestationType", e.target.value)}
                          className={errors.prestationType ? "q-error" : ""}
                          disabled={!data.chantierType}
                        >
                          <option value="">
                            {data.chantierType ? "— Choisir —" : "— Choisir d'abord le type de chantier —"}
                          </option>
                          {prestationOptions.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                        {errors.prestationType ? <div className="q-errText">{errors.prestationType}</div> : null}
                      </div>
                    </div>

                    {data.prestationType === "Autres" && (
                      <div className="q-fields q-one" style={{ marginTop: 12 }}>
                        <div>
                          <label>
                            Précisez <span className="q-req">*</span>
                          </label>
                          <input
                            value={data.prestationOther}
                            onChange={(e) => setField("prestationOther", e.target.value)}
                            className={errors.prestationOther ? "q-error" : ""}
                            placeholder="Merci de préciser..."
                          />
                          {errors.prestationOther ? <div className="q-errText">{errors.prestationOther}</div> : null}
                        </div>
                      </div>
                    )}

                    <div className="q-fields q-two" style={{ marginTop: 12 }}>
                      <div>
                        <label>Type de bâtiment</label>
                        <select value={data.buildingType} onChange={(e) => setField("buildingType", e.target.value)}>
                          <option value="">— Choisir —</option>
                          <option>Maison individuelle</option>
                          <option>Immeuble</option>
                          <option>Local commercial</option>
                          <option>Bâtiment industriel</option>
                          <option>Autre</option>
                        </select>
                      </div>

                      <div>
                        <label>Surface (approx.)</label>
                        <select value={data.surfaceRange} onChange={(e) => setField("surfaceRange", e.target.value)}>
                          <option value="">— Choisir —</option>
                          <option>Moins de 50 m²</option>
                          <option>50–100 m²</option>
                          <option>100–200 m²</option>
                          <option>200–500 m²</option>
                          <option>Plus de 500 m²</option>
                          <option>Je ne sais pas</option>
                        </select>
                      </div>
                    </div>

                    <div className="q-fields q-one" style={{ marginTop: 12 }}>
                      <div>
                        <label>Délais souhaités</label>
                        <select value={data.timeline} onChange={(e) => setField("timeline", e.target.value)}>
                          <option value="">— Choisir —</option>
                          <option>Urgent</option>
                          <option>Sous 1 mois</option>
                          <option>Sous 3 mois</option>
                          <option>Pas de délai précis</option>
                        </select>
                      </div>
                    </div>
                  </section>

                  <div className="q-actions q-actionsPremium">
                    <button type="button" className="q-btn q-btnGhost" onClick={goPrev} disabled={isSending}>
                      Retour
                    </button>
                    <button type="button" className="q-btn q-btnPrimary" onClick={goNext} disabled={isSending}>
                      Continuer
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 3 */}
              {step === 3 && (
                <div className="q-stepPanel">
                  <section className="q-section q-sectionPremium">
                    <div className="q-sectionTitle">
                      <div className="q-badge">3</div>
                      Message & envoi
                    </div>

                    <div className="q-fields q-one">
                      <div>
                        <label>
                          Message (champ libre) <span className="q-req">*</span>
                        </label>
                        <textarea
                          value={data.description}
                          onChange={(e) => setField("description", e.target.value)}
                          className={errors.description ? "q-error" : ""}
                          placeholder="Décris ton besoin : problème, surface, contraintes, accès, urgence…"
                        />
                        {errors.description ? <div className="q-errText">{errors.description}</div> : null}
                      </div>
                    </div>

                    <div style={{ marginTop: 14 }}>
                      <label>Photos (optionnel)</label>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          setField("photos", files);
                        }}
                      />
                      {data.photos.length > 0 ? (
                        <div className="q-files">
                          {data.photos.slice(0, 6).map((f) => (
                            <div key={f.name} className="q-file">
                              {f.name}
                            </div>
                          ))}
                          {data.photos.length > 6 ? (
                            <div className="q-file">+{data.photos.length - 6} fichiers</div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>

                    <div style={{ marginTop: 14 }}>
                      <label className={`q-consent ${errors.consent ? "q-consentError" : ""}`}>
                        <input
                          type="checkbox"
                          checked={data.consent}
                          onChange={(e) => setField("consent", e.target.checked)}
                        />
                        <span>
                          J’accepte que mes données soient utilisées pour être recontacté <span className="q-req">*</span>
                        </span>
                      </label>
                      {errors.consent ? <div className="q-errText">{errors.consent}</div> : null}
                    </div>

                    <div className="q-summary">
                      <div className="q-summaryTitle">Résumé</div>
                      <div className="q-summaryGrid">
                        <div>
                          <div className="k">Client</div>
                          <div className="v">
                            {data.firstName || "—"} {data.lastName || ""}
                          </div>
                          <div className="s">{data.email || "—"}</div>
                          <div className="s">Préférence : {data.callPreference || "Peu importe"}</div>
                        </div>
                        <div>
                          <div className="k">Chantier</div>
                          <div className="v">{data.city || "—"}</div>
                          <div className="s">{data.postalCode || "—"}</div>
                          <div className="s">{data.chantierType || "—"}</div>
                        </div>
                        <div>
                          <div className="k">Prestation</div>
                          <div className="v">{prestationLabel}</div>
                          <div className="s">{data.timeline || "—"}</div>
                        </div>
                      </div>
                    </div>
                  </section>

                  <div className="q-actions q-actionsPremium">
                    <button type="button" className="q-btn q-btnGhost" onClick={goPrev} disabled={isSending}>
                      Retour
                    </button>
                    <button type="submit" className="q-btn q-btnPrimary" disabled={isSending}>
                      {isSending ? "Envoi..." : "Envoyer ma demande"}
                    </button>
                  </div>
                </div>
              )}
            </form>

            <div className="q-bottomFooter">
              <div className="q-bottomFooterLeft">
                <div className="q-bottomTitle">AJ CONSTRUCTION 2 SAVOIE</div>
                <div className="q-bottomSub">Maintenance · Étanchéité · Bardage</div>
              </div>

              <div className="q-bottomFooterRight">
                <a className="q-bottomLink" href="tel:+33743594148">📞 07 43 59 41 48</a>
                <span className="q-bottomSep">•</span>
                <a className="q-bottomLink" href="mailto:contact@ajconstruction2s-btp.fr">
                  ✉️ contact@ajconstruction2s-btp.fr
                </a>
              </div>
            </div>

            <div className="q-footer q-footerPremium">
              <div>RGPD : données utilisées uniquement pour répondre à la demande.</div>
              <div style={{ marginTop: 6, opacity: 0.75, fontSize: 12 }}>
                Propulsé par <b><a className="q-bottomLink" href="https://noxzi.fr">NoxZi</a></b>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}